import { User } from '../types';
import { Pencil, Trash2, UserMinus } from 'lucide-react';
import { SimpleTooltip } from './ui/SimpleTooltip';

interface UserListProps {
  users: User[];
  loading: boolean;
  onEdit: (user: User) => void;
  onDelete: (id: string) => void;
  isGlobalView?: boolean;
}

export function UserList({ users, loading, onEdit, onDelete, isGlobalView = false }: UserListProps) {
  if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-400">Nenhum usuário encontrado</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full divide-y divide-white/5">
                <thead>
                    <tr className="bg-white/5 border-b border-white/10">
                        <th className="px-6 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                            Nome do Usuário
                        </th>
                        <th className="px-6 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                            Endereço de E-mail
                        </th>
                        <th className="px-6 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                            Nível de Acesso
                        </th>
                        <th className="px-6 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                            Status Atual
                        </th>
                        <th className="px-6 py-5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                            Último Acesso
                        </th>
                        <th className="px-6 py-5 text-right text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">
                            Gerenciar
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                    {users.map((user) => (
                        <tr key={user.id} className="hover:bg-white/5 transition-all duration-300 group/row">
                            <td className="px-6 py-5 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20 group-hover/row:scale-110 transition-transform duration-300">
                                        {user.nome ? user.nome.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <div className="text-sm font-bold text-white group-hover/row:text-blue-400 transition-colors">
                                        {user.isPending ? (
                                            <span className="text-gray-500 italic flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
                                                Convite Enviado
                                            </span>
                                        ) : user.nome}
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                                <div className="text-sm text-gray-400 font-medium group-hover/row:text-gray-300 transition-colors truncate max-w-[200px]" title={user.email}>
                                    {user.email}
                                </div>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                                <span className={`inline-flex px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg ${user.role === 'ADMIN'
                                    ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20 shadow-[0_0_10px_rgba(168,85,247,0.1)]'
                                    : 'bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]'
                                    }`}>
                                    {user.role === 'ADMIN' ? 'Administrador' : 'Colaborador'}
                                </span>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                                <span className={`inline-flex px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg ${user.isPending ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 shadow-[0_0_10px_rgba(234,179,8,0.1)]' :
                                    user.ativo
                                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]'
                                        : 'bg-rose-500/10 text-rose-400 border border-rose-500/20 shadow-[0_0_10px_rgba(244,63,94,0.1)]'
                                    }`}>
                                    {user.isPending ? 'Pendente' : (user.ativo ? 'Ativo' : 'Inativo')}
                                </span>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500 font-medium">
                                {user.ultimoLogin
                                    ? new Date(user.ultimoLogin).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : 'Sem registros'
                                }
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity duration-300">
                                    <SimpleTooltip content="Editar Perfil">
                                        <button
                                            onClick={() => onEdit(user)}
                                            disabled={user.isPending}
                                            className={`p-2 rounded-xl transition-all duration-300 ${user.isPending
                                                ? 'text-gray-700 cursor-not-allowed'
                                                : 'text-blue-400 hover:text-white hover:bg-blue-600 hover:shadow-lg hover:shadow-blue-900/40'
                                                }`}
                                        >
                                            <Pencil className="w-4.5 h-4.5" />
                                        </button>
                                    </SimpleTooltip>

                                    <SimpleTooltip content={user.isPending ? "Cancelar Convite" : (isGlobalView ? "Excluir Permanentemente" : "Remover do Workspace")}>
                                        <button
                                            onClick={() => onDelete(user.id)}
                                            className="p-2 text-rose-400 hover:text-white hover:bg-rose-600 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-rose-900/40"
                                        >
                                            {user.isPending || isGlobalView ? <Trash2 className="w-4.5 h-4.5" /> : <UserMinus className="w-4.5 h-4.5" />}
                                        </button>
                                    </SimpleTooltip>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}